<template>
  <Popup v-model='cart.isOpen' panelClass='cart-panel'>
    <div class='cart-inner'>

      <!-- Scrollable top: title + items -->
      <div class='cart-top'>
        <h2>Cart</h2>
        <hr />
        <div class='cart-items'>
          <p class='empty' v-if='!cart.line_items.length'>Your cart is empty.</p>
          <div class='cart-item' v-for='item in cart.line_items' :key='item._slug'>
            <img v-if='item.price_data.product_data.images[0]' :src='item.price_data.product_data.images[0]' :alt='item.price_data.product_data.name' class='item-cover' />
            <div class='item-details'>
              <div class='item-title-row'>
                <p class='item-title'>{{ item.price_data.product_data.name }}</p>
                <button class='remove-btn' @click='cart.removeItem(item._slug)' title='Remove'>✕</button>
              </div>
              <div class='item-meta'>
                <span class='meta-label'>Quantity</span>
                <select class='pill-select' :value='item.quantity' @change='cart.updateQuantity(item._slug, +$event.target.value)'>
                  <option v-for='n in 10' :key='n' :value='n'>{{ n }}</option>
                </select>
                <span class='item-price'>${{ ((item.price_data.unit_amount / 100) * item.quantity).toFixed(2) }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Sticky footer: order type, shipping, tax, subtotal, checkout -->
      <div class='cart-footer'>
        <hr />

        <div class='cart-row'>
          <span class='meta-label'>Order type</span>
          <select class='pill-select' v-model='cart.orderType'>
            <option value='individual'>Individual</option>
            <option value='institutional'>Institutional</option>
          </select>
        </div>

        <hr />

        <div class='cart-row'>
          <span class='meta-label'>Shipping</span>
          <select class='pill-select' v-model='cart.selectedRateId' @change='cart.loadSession()'>
            <option v-if='!cart.shippingRates.length' disabled value=''>Loading…</option>
            <option v-for='rate in cart.shippingRates' :key='rate.id' :value='rate.id'>
              {{ rate.name }} — ${{ (rate.amount / 100).toFixed(2) }}
            </option>
          </select>
        </div>

        <p class='tax-note'>Tax <span class='muted'>Determined on the Checkout Page</span></p>

        <hr />

        <div class='cart-row subtotal'>
          <span class='meta-label'>Subtotal</span>
          <span class='subtotal-amount'>${{ cart.subtotal.toFixed(2) }}</span>
        </div>

        <hr />

        <p class='session-error' v-if='cart.sessionError'>
          Online checkout unavailable — <a href='mailto:shop@publictrust.org'>email us to order</a>
        </p>
        <button class='checkout-btn' :disabled='!cart.line_items.length' @click='cart.goToCheckout()'>Checkout</button>
      </div>

    </div>
  </Popup>
</template>

<script setup>
  import { onMounted, onUnmounted } from 'vue'
  import Popup from './Popup.vue'
  import { useCartStore } from './CartStore.js'
  const cart = useCartStore();

  const GAP = 24; // px below header

  function setCartTop() {
    const header = document.querySelector('header');
    if (header) {
      const bottom = header.getBoundingClientRect().bottom;
      document.documentElement.style.setProperty('--cart-top', `${bottom + GAP}px`);
    }
  }

  let ro;
  onMounted(() => {
    setCartTop();
    ro = new ResizeObserver(setCartTop);
    const header = document.querySelector('header');
    if (header) ro.observe(header);
  });
  onUnmounted(() => ro?.disconnect());
</script>

<!-- Panel positioning — non-scoped so it reaches Popup's root div -->
<style>
  .cart-panel {
    top: var(--cart-top, 12rem) !important;
    right: 1.5rem !important;
    bottom: 1.5rem !important;
    width: 38rem !important;
    padding: 3rem !important;
    background-color: #ffb004 !important;
    border-radius: 2.5rem !important;
    display: flex;
    flex-direction: column !important;
    overflow: hidden !important;
    color: var(--first) !important;
    border: none !important;
    box-shadow: -0.5rem 0 2rem rgba(74, 47, 48, 0.15) !important;
  }

  .cart-panel svg.close .circle { fill: rgba(74, 47, 48, 0.15); }
  .cart-panel svg.close .cross { fill: var(--first); }
</style>

<style scoped>
  .cart-inner {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
  }

  .cart-top {
    flex: 1;
    overflow-y: auto;
    min-height: 0;
  }

  .cart-footer {
    flex-shrink: 0;
  }

  h2 {
    font-family: 'Copernicus', serif;
    font-size: 2.4rem;
    font-weight: normal;
    margin: 0 0 1.5rem;
    padding-right: 3rem;
    color: var(--first);
  }

  hr {
    border: none;
    border-top: 1px solid rgba(74, 47, 48, 0.25);
    margin: 1.5rem 0;
  }

  .cart-items {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
  }

  .empty {
    font-size: var(--ui);
    opacity: 0.6;
  }

  .cart-item {
    display: flex;
    gap: 1.5rem;
    align-items: flex-start;
  }

  .item-cover {
    width: 7rem;
    flex-shrink: 0;
    object-fit: contain;
    display: block;
  }

  .item-details {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 0.8rem;
  }

  .item-title-row {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 0.5rem;
  }

  .item-title {
    font-family: 'Copernicus', serif;
    font-size: 1.6rem;
    line-height: 1.2;
    color: var(--first);
    flex: 1;
  }

  .remove-btn {
    background: none;
    border: none;
    color: var(--first);
    opacity: 0.4;
    cursor: pointer;
    font-size: 1.1rem;
    padding: 0;
    line-height: 1;
    flex-shrink: 0;
    transition: opacity 0.15s;
  }

  .remove-btn:hover {
    opacity: 1;
  }

  .item-meta {
    display: flex;
    align-items: center;
    gap: 1rem;
  }

  .cart-row {
    display: flex;
    align-items: center;
    gap: 1rem;
  }

  .cart-row.subtotal {
    justify-content: space-between;
  }

  .meta-label {
    font-family: "Maison Neue Demi", sans-serif;
    font-size: var(--ui);
    color: var(--first);
  }

  .item-price,
  .subtotal-amount {
    font-family: "Maison Neue Demi", sans-serif;
    font-size: var(--ui);
    color: var(--first);
    margin-left: auto;
  }

  .pill-select {
    font-family: "Maison Neue Demi", sans-serif;
    font-size: var(--ui);
    color: var(--first);
    background: transparent;
    border: 1px solid var(--first);
    border-radius: 2em;
    padding: 0.2em 1.2em 0.2em 0.8em;
    appearance: none;
    -webkit-appearance: none;
    cursor: pointer;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='8' height='5' viewBox='0 0 8 5'%3E%3Cpath d='M0 0l4 5 4-5z' fill='%234a2f30'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: left 0.6em center;
    padding-left: 1.8em;
  }

  .pill-select:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(74, 47, 48, 0.3);
  }

  .tax-note {
    font-family: "Maison Neue Demi", sans-serif;
    font-size: var(--ui);
    color: var(--first);
    margin-top: 0.8rem;
  }

  .muted { opacity: 0.45; }

  .session-error {
    font-family: "Maison Neue Demi", sans-serif;
    font-size: var(--ui);
    color: var(--first);
    opacity: 0.7;
    text-align: center;
    margin-bottom: 0.5rem;
  }

  .session-error a {
    text-decoration: underline;
    color: var(--first);
  }

  .checkout-btn {
    display: block;
    width: fit-content;
    margin: 0 auto;
    background: transparent;
    border: 1px solid rgba(74, 47, 48, 0.7);
    color: var(--first);
    font-family: "Maison Neue Demi", sans-serif;
    font-size: var(--ui);
    border-radius: 2em;
    padding: 0.55em 2em 0.6em;
    cursor: pointer;
    transition: background 0.2s;
  }

  .checkout-btn:disabled {
    opacity: 0.4;
    cursor: default;
  }

  .checkout-btn:hover:not(:disabled) {
    background: rgba(74, 47, 48, 0.1);
  }
</style>
